import { useRef, useEffect, useState } from 'react';

const getViableTotalCnt = (num: number) => Math.min(Math.max(num, 1), 100000);

const useLoadingProgress = ({
  resCnt,
  totalResCnt,
  duration = 4000,
  moreFlag = false,
}: {
  resCnt: number;
  totalResCnt: number;
  duration?: number;
  moreFlag?: boolean;
}) => {
  // 20ms : x = duration : 100 => x = 20 * 100 / duration;
  const [progress, setProgress] = useState(0);
  const progressTimer = useRef<any>(null);
  const cnt = useRef<number>(resCnt || 0);
  const tot = useRef<number>(getViableTotalCnt(totalResCnt));
  const curr = useRef<number>((cnt.current / tot.current) * 100);
  const [isOnLoading, setIsOnLoading] = useState<boolean>(true);
  let currUnit = 2000 / duration;
  let fakeCurr = curr.current || 0;

  const updateProgress = () => {
    /*
      fakeProgress는 100%까지 {duration}ms동안 등속도로 진행.
      curr값보다 fake값이 큰 경우에만 해당 값을 적용.
      {duration}ms동안 응답이 없는 경우엔 NO RESULT 처리하도록 함.
      cache가 있어서 로딩시간이 짧을 경우(같은 목록을 이전에 로딩했던 경우): duration을 줄여줌.
    */
    if (!moreFlag || (tot.current > 0 && tot.current === cnt.current)) {
      currUnit *= 1.2;
    }

    fakeCurr = Math.min(
      Math.max(curr.current, fakeCurr) + currUnit,
      isOnLoading ? 90 : 100,
    ); // fake는 loading 전엔 90%까지만 표기.

    const indicatedProgress = Math.max(fakeCurr, curr.current);

    if (indicatedProgress >= 100) {
      clearInterval(progressTimer.current);
      setIsOnLoading(false);
      setProgress(100);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      setIsOnLoading(indicatedProgress < 100);
      setProgress(indicatedProgress);
    }
  };

  const initiate = (res: number = 0, totRes: number = 1) => {
    cnt.current = res;
    tot.current = getViableTotalCnt(totRes);
    currUnit = 2000 / duration;
    curr.current = (res / tot.current) * 100;
    fakeCurr = curr.current || 0;
    if (progressTimer.current) clearInterval(progressTimer.current);
    progressTimer.current = setInterval(updateProgress, 20);
    setIsOnLoading(true);
  };

  useEffect(() => {
    if (resCnt < 1 && moreFlag) {
      initiate(resCnt, totalResCnt);
    }
  }, [moreFlag]);

  const setLoading = (isLoading: boolean) => {
    setIsOnLoading(isLoading);
    if (!isLoading) {
      clearInterval(progressTimer.current);
      setProgress(100);
    } else initiate(resCnt, totalResCnt);
  };

  useEffect(() => {
    cnt.current = resCnt;
    tot.current = getViableTotalCnt(totalResCnt);
    curr.current += (cnt.current / tot.current) * (100 - curr.current);
  }, [resCnt, totalResCnt]);

  useEffect(() => {
    return () => {
      clearInterval(progressTimer.current);
    };
  }, []);

  return {
    initiate,
    progress,
    isOnLoading,
    setLoading,
  };
};

export default useLoadingProgress;

const CopyrightWhiteList = new Set([
  'wikimedia',
  'pixabay',
  'flickr',
  'wikipedia',
]);
export const shouldShowCopyright = (
  copyright?: string,
  copyrightUrl?: string,
) => copyright && copyrightUrl && CopyrightWhiteList.has(copyright);
